<template>
  <transition name="nps-slide">
    <div class="nps-widget" v-if="visible" role="dialog" :aria-label="$t('nps.question')">
      <button class="nps-widget__close" @click="dismiss" aria-label="Cerrar encuesta">×</button>

      <!-- Step 1: Score NPS -->
      <div v-if="step === 1" class="nps-widget__step">
        <p class="nps-widget__question">{{ $t('nps.question') }}</p>
        <div class="nps-widget__scale">
          <button
            v-for="n in 11"
            :key="n - 1"
            :class="['nps-widget__score-btn', { 'is-selected': score === n - 1 }]"
            @click="submitScore(n - 1)"
          >{{ n - 1 }}</button>
        </div>
        <div class="nps-widget__scale-labels">
          <span>{{ $t('nps.scaleLabelMin') }}</span>
          <span>{{ $t('nps.scaleLabelMax') }}</span>
        </div>
      </div>

      <!-- Step 2: Pregunta cualitativa -->
      <div v-if="step === 2" class="nps-widget__step">
        <p class="nps-widget__question">{{ qualitativeQuestion }}</p>
        <textarea
          v-model="qualitativeText"
          class="nps-widget__textarea"
          :placeholder="$t('nps.textareaPlaceholder')"
          rows="3"
        ></textarea>
        <div class="nps-widget__actions">
          <button class="nps-widget__btn-skip" @click="advanceStep">{{ $t('nps.skip') }}</button>
          <button class="nps-widget__btn-primary" @click="submitQualitative">{{ $t('nps.continue') }}</button>
        </div>
      </div>

      <!-- Step 3: Reseña (solo score >= 8 y app) -->
      <div v-if="step === 3" class="nps-widget__step">
        <p class="nps-widget__question">{{ $t('nps.reviewQuestion') }}</p>
        <p class="nps-widget__subtitle">{{ $t('nps.reviewSubtitle') }}</p>
        <div class="nps-widget__actions nps-widget__actions--spread">
          <button class="nps-widget__btn-done" @click="advanceStep">{{ $t('nps.reviewNotNow') }}</button>
          <a class="nps-widget__btn-primary" :href="reviewUrl" @click="advanceStep">{{ $t('nps.reviewCta') }}</a>
        </div>
      </div>

      <!-- Step 4: Rating facilidad de uso (placeholder) -->
      <div v-if="step === 4" class="nps-widget__step">
        <p class="nps-widget__question">{{ $t('nps.question') }}</p>
        <div class="nps-widget__stars">
          <button
            v-for="n in 5"
            :key="n"
            :class="['nps-widget__star', { 'is-active': starRating >= n }]"
            @click="starRating = n"
          >★</button>
        </div>
        <div class="nps-widget__actions nps-widget__actions--spread">
          <button class="nps-widget__btn-done" @click="finish">{{ $t('nps.skip') }}</button>
          <button class="nps-widget__btn-primary" @click="submitStarRating" :disabled="!starRating">{{ $t('nps.continue') }}</button>
        </div>
      </div>

      <!-- Step 5: Opción múltiple (placeholder) -->
      <div v-if="step === 5" class="nps-widget__step">
        <p class="nps-widget__question">{{ $t('nps.question') }}</p>
        <div class="nps-widget__choices">
          <button
            v-for="opt in usageOptions"
            :key="opt.value"
            :class="['nps-widget__choice', { 'is-selected': selectedUsage === opt.value }]"
            @click="selectedUsage = opt.value"
          >{{ opt.label }}</button>
        </div>
        <div class="nps-widget__actions nps-widget__actions--spread">
          <button class="nps-widget__btn-done" @click="finish">{{ $t('nps.skip') }}</button>
          <button class="nps-widget__btn-primary" @click="submitUsage" :disabled="!selectedUsage">{{ $t('nps.continue') }}</button>
        </div>
      </div>

      <!-- Step final: Gracias -->
      <div v-if="step === 6" class="nps-widget__step nps-widget__step--thanks">
        <div class="nps-widget__thanks-icon">🎉</div>
        <p class="nps-widget__thanks-title">{{ $t('nps.thanksTitle') }}</p>
        <p class="nps-widget__thanks-text">{{ $t('nps.thanksText') }}</p>
        <button class="nps-widget__btn-primary" @click="close">{{ $t('nps.close') }}</button>
      </div>
    </div>
  </transition>
</template>

<script>
const SESSION_KEY = 'nps_shown';

import { mapGetters } from 'vuex';

export default {
  name: 'NpsWidget',

  computed: {
    ...mapGetters(['isApp']),
    qualitativeQuestion() {
      return this.score >= 8 ? this.$t('nps.qualitativePromoter') : this.$t('nps.qualitativeDetractor');
    },
    platform() {
      const ua = navigator.userAgent;
      if (/android/i.test(ua)) return 'android';
      if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
      return null;
    },
    reviewUrl() {
      if (this.platform === 'android') return 'market://details?id=com.hellobb.app&utm_source=hellowish';
      if (this.platform === 'ios') return 'https://apps.apple.com/app/hellowish/id1580704090?utm_source=hellowish';
      return null;
    },
    showReviewStep() {
      return this.score >= 8 && this.isApp && this.reviewUrl !== null;
    },
  },

  data() {
    return {
      visible: false,
      step: 1,
      score: null,
      responseId: null,
      qualitativeText: '',
      starRating: 0,
      selectedUsage: null,
      usageOptions: [
        { value: 'lista', label: 'Gestionar mi lista' },
        { value: 'catalogo', label: 'Explorar el catálogo' },
        { value: 'colaboracion', label: 'Compartir con familia' },
        { value: 'hucha', label: 'Hucha de regalos' },
      ],
    };
  },

  methods: {
    async checkAndShow() {
      if (sessionStorage.getItem(SESSION_KEY)) return;

      const { eligible } = await this.$store.dispatch('checkNpsEligibility');
      if (!eligible) return;

      this.mountedAt = Date.now();
      this._onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this._onScroll, { passive: true });
    },

    onScroll() {
      const elapsed = (Date.now() - this.mountedAt) / 1000;
      if (elapsed < 15) return;

      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      const pct = scrolled / total;
      if (pct < 0.3) return;

      this.show();
    },

    show() {
      window.removeEventListener('scroll', this._onScroll);
      sessionStorage.setItem(SESSION_KEY, '1');
      this.visible = true;
    },

    async submitScore(value) {
      this.score = value;
      try {
        const data = await this.$store.dispatch('createNpsResponse', { score: value });
        this.responseId = data?.id ?? null;
      } catch {
        // continuamos aunque falle la API
      }
      this.advanceStep();
    },

    async submitQualitative() {
      if (this.responseId) {
        try {
          await this.$store.dispatch('updateNpsResponse', {
            id: this.responseId,
            payload: { qualitativeText: this.qualitativeText },
          });
        } catch { /* continuar igualmente */ }
      }
      this.advanceStep();
    },

    async submitStarRating() {
      if (this.responseId) {
        try {
          await this.$store.dispatch('updateNpsResponse', {
            id: this.responseId,
            payload: { easeOfUseRating: this.starRating },
          });
        } catch { /* continuar igualmente */ }
      }
      this.advanceStep();
    },

    async submitUsage() {
      if (this.responseId) {
        try {
          await this.$store.dispatch('updateNpsResponse', {
            id: this.responseId,
            payload: { mostUsedFeature: this.selectedUsage },
          });
        } catch { /* continuar igualmente */ }
      }
      this.advanceStep();
    },

    advanceStep() {
      const next = this.step + 1;
      if (next === 3 && !this.showReviewStep) {
        // sin reseña: saltar directo a gracias
        this.step = 6;
      } else if (next === 4) {
        // preguntas adicionales desactivadas: saltar a gracias
        this.step = 6;
      } else {
        this.step = next;
      }
    },

    async finish() {
      if (this.responseId) {
        try {
          await this.$store.dispatch('updateNpsResponse', {
            id: this.responseId,
            payload: { status: 'completed' },
          });
        } catch { /* continuar igualmente */ }
      }
      this.step = 6;
    },

    async close() {
      try {
        await this.$store.dispatch('updateNpsResponse', {
          id: this.responseId,
          payload: { status: 'completed' },
        });
      } catch { /* continuar igualmente */ }
      this.visible = false;
    },

    async dismiss() {
      try {
        await this.$store.dispatch('dismissNps');
      } catch { /* continuar igualmente */ }
      this.visible = false;
    },
  },

  mounted() {
    this.mountedAt = null;
    this._onScroll = null;
    this.checkAndShow();
  },

  beforeDestroy() {
    if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
  },
};
</script>

<style scoped>
.nps-widget {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #ffffff;
  border-top: 3px solid #ffc944;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.18);
  z-index: 950;
  padding: 20px 20px 28px;
  max-width: 560px;
  margin: 0 auto;
  border-radius: 16px 16px 0 0;
}

.nps-widget__close {
  position: absolute;
  top: 12px;
  right: 14px;
  background: none;
  border: none;
  font-size: 22px;
  line-height: 1;
  color: #999;
  cursor: pointer;
  padding: 0;
}
.nps-widget__close:hover {
  color: #333;
}

.nps-widget__step {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.nps-widget__question {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  padding-right: 24px;
  line-height: 1.4;
}

.nps-widget__subtitle {
  font-size: 13px;
  color: #666;
  margin: -8px 0 0;
  line-height: 1.4;
}

.nps-widget__scale {
  display: flex;
  gap: 4px;
}

.nps-widget__score-btn {
  flex: 1;
  min-height: 36px;
  border-radius: 8px;
  border: 1.5px solid #d0d0d0;
  background: #fff;
  font-size: 13px;
  font-weight: 600;
  color: #444;
  cursor: pointer;
  transition: all 0.15s;
  padding: 0;
}
.nps-widget__score-btn:hover {
  border-color: #ffc944;
  background: #fffbeb;
  color: #1e0028;
}
.nps-widget__score-btn.is-selected {
  background: #ffc944;
  border-color: #ffc944;
  color: #1e0028;
}

.nps-widget__scale-labels {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #999;
  margin-top: -8px;
}

.nps-widget__textarea {
  width: 100%;
  border: 1.5px solid #d0d0d0;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  color: #333;
  box-sizing: border-box;
  transition: border-color 0.15s;
}
.nps-widget__textarea:focus {
  outline: none;
  border-color: #ffc944;
}

.nps-widget__stars {
  display: flex;
  gap: 8px;
}

.nps-widget__star {
  background: none;
  border: none;
  font-size: 32px;
  color: #d0d0d0;
  cursor: pointer;
  padding: 0;
  transition: color 0.1s;
  line-height: 1;
}
.nps-widget__star.is-active {
  color: #ffc944;
}

.nps-widget__choices {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.nps-widget__choice {
  border: 1.5px solid #d0d0d0;
  border-radius: 8px;
  background: #fff;
  padding: 10px 14px;
  text-align: left;
  font-size: 14px;
  color: #444;
  cursor: pointer;
  transition: all 0.15s;
}
.nps-widget__choice:hover {
  border-color: #ffc944;
  background: #fffbeb;
  color: #1e0028;
}
.nps-widget__choice.is-selected {
  border-color: #ffc944;
  background: #fffbeb;
  color: #1e0028;
  font-weight: 600;
}

.nps-widget__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.nps-widget__actions--spread {
  justify-content: space-between;
}

.nps-widget__btn-primary {
  background: #ffc944;
  color: #1e0028;
  border: none;
  border-radius: 50px;
  padding: 10px 22px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.nps-widget__btn-primary:hover:not(:disabled) {
  background: #f5be30;
}
.nps-widget__btn-primary:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.nps-widget__btn-skip {
  background: none;
  border: none;
  color: #999;
  font-size: 14px;
  cursor: pointer;
  padding: 10px 8px;
}
.nps-widget__btn-skip:hover {
  color: #555;
}

.nps-widget__btn-done {
  background: none;
  border: 1.5px solid #d0d0d0;
  border-radius: 50px;
  color: #777;
  font-size: 14px;
  padding: 10px 16px;
  cursor: pointer;
  transition: all 0.15s;
}
.nps-widget__btn-done:hover {
  border-color: #999;
  color: #444;
}

.nps-widget__step--thanks {
  align-items: center;
  text-align: center;
}

.nps-widget__thanks-icon {
  font-size: 36px;
}

.nps-widget__thanks-title {
  font-size: 17px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
}

.nps-widget__thanks-text {
  font-size: 14px;
  color: #666;
  margin: 0;
  line-height: 1.5;
}

.nps-slide-enter-active,
.nps-slide-leave-active {
  transition: transform 0.35s ease, opacity 0.35s ease;
}
.nps-slide-enter,
.nps-slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

@media (min-width: 600px) {
  .nps-widget {
    left: 50%;
    right: auto;
    transform: translateX(-50%);
    bottom: 24px;
    border-radius: 16px;
    border-top: 3px solid #ffc944;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.22);
    max-width: 720px;
    width: 90%;
  }

  .nps-slide-enter,
  .nps-slide-leave-to {
    transform: translateX(-50%) translateY(20px);
    opacity: 0;
  }

  .nps-slide-enter-active,
  .nps-slide-leave-active {
    transition: transform 0.3s ease, opacity 0.3s ease;
  }
}
</style>
